import { useState, useEffect, useCallback, useMemo } from 'react'
import CodeMirror from './common/CodeMirrorWithWrap'
import { markdown } from '@codemirror/lang-markdown'
import { json } from '@codemirror/lang-json'
import { sql as sqlLang } from '@codemirror/lang-sql'
import { yaml as yamlLang } from '@codemirror/lang-yaml'
import { oneDark } from '@codemirror/theme-one-dark'
import { EditorView } from '@codemirror/view'
import { api } from '../api'
import { APP_DEFAULTS } from '../defaults'
import SearchInput from './common/SearchInput'
import HighlightText from './common/HighlightText'
import TokenBadge from './common/TokenBadge'
import { filterByText } from '../utils/filterByText'
import { downloadText, ensureExtension } from '../utils/download'
import './VirtualSchemaViewer.css'

/**
 * Virtual Schema Viewer (KBNL-22).
 *
 * 좌측: 전체 schema 목록 (모든 사용자 것)
 *       각 항목에 owner / 활성 버전 표시 + "사용" 토글 (settings.selected_virtual_schema_ids 와 동기화)
 * 우측: 2 panel 에디터
 *       - 좌 panel (원본): CodeMirror — 본인/admin 만 편집 가능
 *       - 우 panel (저장본): 활성 버전 (원본 또는 요약) 표시 + 요약은 본인/admin 편집 가능
 *       버튼 (본인/admin 만): 원본 적용 · 요약해서 적용 · 저장 · 🔄 요약 재생성 · 🗑 삭제
 *       다른 사람 것: 모든 버튼 숨김 (admin 일 때만 일부 노출)
 *
 * 권한:
 *   본인 또는 admin → 편집/저장/삭제/요약 재생성/active 변경
 *   그 외 → 열람 + 본인 settings 에 "사용" 토글만
 */

// Firestore timestamp (ISO 또는 epoch) → 'YYYY-MM-DD HH:MM' 한 줄 표시
const fmtTime = (v) => {
  if (!v) return ''
  try {
    const d = (typeof v === 'number') ? new Date(v * 1000) : new Date(v)
    if (isNaN(d.getTime())) return ''
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  } catch {
    return ''
  }
}

// 포맷별 CodeMirror 언어 확장 매핑 (없으면 plain text)
const FORMAT_OPTIONS = ['tsv', 'csv', 'txt', 'md', 'json', 'sql', 'yaml']
const _formatExtension = (fmt) => {
  switch ((fmt || '').toLowerCase()) {
    case 'md':
    case 'markdown':
      return [markdown()]
    case 'json':
      return [json()]
    case 'sql':
      return [sqlLang()]
    case 'yaml':
    case 'yml':
      return [yamlLang()]
    default:
      return []  // tsv/csv/txt → plain
  }
}

const cmBaseTheme = EditorView.theme({
  '&':            { fontSize: 'var(--code-font-size, 13px)' },
  '.cm-content':  { fontFamily: 'var(--font-code)', padding: '8px 0' },
  '.cm-gutters':  { fontFamily: 'var(--font-code)' },
  '.cm-line':     { padding: '0 10px' },
  '.cm-scroller': { overflow: 'auto' },
})

export default function VirtualSchemaViewer({
  showToast,
  settings,
  setSettings,
  authUser,
  editorTheme,
}) {
  const [items, setItems] = useState([])
  const [loadingList, setLoadingList] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [detail, setDetail] = useState(null)        // 서버 원본 (저장된 상태)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [saving, setSaving] = useState(false)
  const [summarizing, setSummarizing] = useState(false)

  // 신규 생성 모드 (selectedId === null && creating)
  const [creating, setCreating] = useState(false)

  // 로컬 편집 상태 (저장 전)
  // 우측(저장본/요약) panel 이 LLM 에 들어가는 내용. 좌측(원본) 은 참고/소스.
  // active_version 개념은 UI 에서 제거됨 — summary 가 비어있으면 LLM 이 original 로 fallback.
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')           // schema 설명/메모
  const [editOriginalFormat, setEditOriginalFormat] = useState('tsv')   // 원본 panel 포맷
  const [editSummaryFormat, setEditSummaryFormat]   = useState('yaml')  // 저장본 panel 포맷
  const [editOriginal, setEditOriginal] = useState('')
  const [editSummary, setEditSummary] = useState('')

  // 인라인 rename 상태 — 좌측 list 의 항목 이름 변경 (본인/admin 만)
  const [renamingDocId, setRenamingDocId] = useState(null)
  const [renameValue, setRenameValue] = useState('')

  // 검색 / 필터
  const [query, setQuery] = useState('')
  const [mineOnly, setMineOnly] = useState(false)

  // 인증 사용자 식별 — filteredItems / canEditSelected 보다 먼저 선언해야 한다 (TDZ 방지).
  const userId = authUser?.user_id || ''
  const isAdmin = !!authUser?.is_admin

  // items → 검색 + "내 것만" 필터 적용
  const filteredItems = useMemo(() => {
    let arr = items
    if (mineOnly && userId) {
      arr = arr.filter(it => it.owner_user_id === userId)
    }
    return filterByText(arr, query, ['name', 'description', 'owner_name', 'owner_user_id'])
  }, [items, query, mineOnly, userId])

  const isOwnerOrAdmin = useCallback((item) => {
    if (!item) return false
    if (isAdmin) return true
    return item.owner_user_id === userId
  }, [userId, isAdmin])

  const canEditSelected = useMemo(() => {
    if (creating) return true
    if (!detail) return false
    return isOwnerOrAdmin(detail)
  }, [creating, detail, isOwnerOrAdmin])

  // dirty 체크 — 서버 상태와 로컬 편집 상태 비교
  const dirty = useMemo(() => {
    if (creating) return !!(editName.trim() || editOriginal || editSummary || editDescription)
    if (!detail) return false
    return (
      editDescription !== (detail.description || '') ||
      editOriginalFormat !== (detail.original_format || detail.format || 'tsv') ||
      editSummaryFormat !== (detail.summary_format || 'yaml') ||
      editOriginal !== (detail.original || '') ||
      editSummary !== (detail.summary || '')
    )
  }, [creating, detail, editName, editDescription, editOriginalFormat, editSummaryFormat, editOriginal, editSummary])

  // 선택된 schema 가 사용자 settings 에 등록돼 있는지
  const selectedIds = settings?.selected_virtual_schema_ids || []
  const isSelectedInSettings = useCallback((docId) => selectedIds.includes(docId), [selectedIds])

  // ── 목록 로드 ────────────────────────────────────────────────────────────
  const refreshList = useCallback(async () => {
    setLoadingList(true)
    try {
      const res = await api.listVirtualSchemas()
      setItems(res.items || [])
    } catch (e) {
      showToast?.('error', `Virtual Schema 목록 로드 실패: ${e.message}`)
    } finally {
      setLoadingList(false)
    }
  }, [showToast])

  useEffect(() => { refreshList() }, [refreshList])

  // ── 단건 로드 ────────────────────────────────────────────────────────────
  const loadDetail = useCallback(async (docId) => {
    if (!docId) return
    setLoadingDetail(true)
    try {
      const d = await api.getVirtualSchema(docId)
      setDetail(d)
      setEditName(d.name || '')
      setEditDescription(d.description || '')
      setEditOriginalFormat(d.original_format || d.format || 'tsv')
      setEditSummaryFormat(d.summary_format || 'yaml')
      setEditOriginal(d.original || '')
      setEditSummary(d.summary || '')
    } catch (e) {
      showToast?.('error', `로드 실패: ${e.message}`)
      setDetail(null)
    } finally {
      setLoadingDetail(false)
    }
  }, [showToast])

  const confirmDiscardIfDirty = () => {
    if (!dirty) return true
    return confirm('저장하지 않은 변경이 있습니다. 버릴까요?')
  }

  const handleSelect = (docId) => {
    if (docId === selectedId && !creating) return
    if (!confirmDiscardIfDirty()) return
    setCreating(false)
    setSelectedId(docId)
    loadDetail(docId)
  }

  const handleNew = () => {
    if (!confirmDiscardIfDirty()) return
    setCreating(true)
    setSelectedId(null)
    setDetail(null)
    setEditName('')
    setEditDescription('')
    setEditOriginalFormat('tsv')
    setEditSummaryFormat('yaml')
    setEditOriginal('')
    setEditSummary('')
  }

  // ── 저장 (생성 / 편집) ──────────────────────────────────────────────────
  const handleSave = async () => {
    if (creating) {
      const name = editName.trim()
      if (!name) {
        showToast?.('error', '이름을 입력하세요.')
        return
      }
      setSaving(true)
      try {
        const res = await api.createVirtualSchema({
          name,
          original: editOriginal,
          description: editDescription,
          original_format: editOriginalFormat,
          summary_format: editSummaryFormat,
          summary: editSummary || null,
        })
        showToast?.('success', `생성: ${res.name}`)
        await refreshList()
        setCreating(false)
        setSelectedId(res.doc_id)
        await loadDetail(res.doc_id)
      } catch (e) {
        showToast?.('error', `생성 실패: ${e.message}`)
      } finally {
        setSaving(false)
      }
    } else if (detail) {
      setSaving(true)
      try {
        await api.updateVirtualSchema(detail.doc_id, {
          original: editOriginal,
          description: editDescription,
          original_format: editOriginalFormat,
          summary_format: editSummaryFormat,
          summary: editSummary,
        })
        showToast?.('success', `저장: ${detail.name}`)
        await refreshList()
        await loadDetail(detail.doc_id)
      } catch (e) {
        showToast?.('error', `저장 실패: ${e.message}`)
      } finally {
        setSaving(false)
      }
    }
  }

  // ── 삭제 ──────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!detail) return
    if (!isOwnerOrAdmin(detail)) return
    if (!confirm(`'${detail.name}' 을 삭제할까요?`)) return
    try {
      await api.deleteVirtualSchema(detail.doc_id)
      showToast?.('success', `삭제: ${detail.name}`)
      // settings 에서도 자동 제거 (stale id 정리)
      const newIds = selectedIds.filter(id => id !== detail.doc_id)
      if (newIds.length !== selectedIds.length) {
        try {
          await api.saveUserSettings({ ...settings, selected_virtual_schema_ids: newIds })
          setSettings({ ...settings, selected_virtual_schema_ids: newIds })
        } catch {}
      }
      await refreshList()
      setSelectedId(null)
      setDetail(null)
    } catch (e) {
      showToast?.('error', `삭제 실패: ${e.message}`)
    }
  }

  // ── 원본 그대로 적용 — 우측 panel ← 좌측 원본 (로컬 복사, 저장은 별도) ──
  const handleApplyOriginal = () => {
    setEditSummaryFormat(editOriginalFormat)
    setEditSummary(editOriginal)
  }

  // ── 요약해서 적용 — LLM 호출 → 우측 panel 채움 (로컬, 저장은 별도) ──
  // creating 모드에서는 doc_id 가 없어서 summarize endpoint 사용 불가 → 비활성
  const handleApplySummary = async () => {
    if (creating) {
      showToast?.('error', '먼저 schema 를 저장한 뒤 요약 가능합니다.')
      return
    }
    if (!detail) return
    if (!editOriginal.trim()) {
      showToast?.('error', '원본이 비어있어 요약할 수 없습니다.')
      return
    }
    if (!settings?.project_id) {
      showToast?.('error', '설정에서 GCP Project ID 가 필요합니다.')
      return
    }
    setSummarizing(true)
    try {
      const res = await api.summarizeVirtualSchema(detail.doc_id, {
        project_id: settings.project_id,
        location: settings.vertex_location || settings.location || APP_DEFAULTS.location,
        model_name: settings.model_name || APP_DEFAULTS.model_name,
      })
      // 서버가 summary 를 저장하고 결과 반환 → 로컬도 동기화
      setEditSummary(res.summary || '')
      showToast?.('success', `요약 생성 (${res.summary_len} 자)`)
      await refreshList()
      await loadDetail(detail.doc_id)
    } catch (e) {
      showToast?.('error', `요약 실패: ${e.message}`)
    } finally {
      setSummarizing(false)
    }
  }

  // ── 인라인 rename ────────────────────────────────────────────────────────
  const startRename = (item) => {
    if (!isOwnerOrAdmin(item)) return
    setRenamingDocId(item.doc_id)
    setRenameValue(item.name)
  }
  const cancelRename = () => {
    setRenamingDocId(null)
    setRenameValue('')
  }
  const commitRename = async () => {
    const target = items.find(i => i.doc_id === renamingDocId)
    const newName = (renameValue || '').trim()
    if (!target) { cancelRename(); return }
    if (!newName || newName === target.name) { cancelRename(); return }
    if (newName.includes('__')) {
      showToast?.('error', '이름에 "__" 는 사용할 수 없습니다.')
      return
    }
    try {
      const res = await api.renameVirtualSchema(target.doc_id, newName)
      showToast?.('success', `이름 변경: ${target.name} → ${res.name}`)
      const wasSelected = selectedId === target.doc_id
      cancelRename()
      await refreshList()
      if (wasSelected) {
        setSelectedId(res.doc_id)
        await loadDetail(res.doc_id)
      }
      // App 의 settings 도 동기화 (백엔드에서 sync 했으니 새로 fetch)
      try {
        const us = await api.getUserSettings()
        if (us?.settings) setSettings(us.settings)
      } catch {}
    } catch (e) {
      showToast?.('error', `이름 변경 실패: ${e.message}`)
    }
  }

  // ── settings 의 "사용" 토글 ───────────────────────────────────────────────
  const handleToggleUse = async (item) => {
    const docId = item.doc_id
    const cur = settings?.selected_virtual_schema_ids || []
    const next = cur.includes(docId)
      ? cur.filter(id => id !== docId)
      : [...cur, docId]
    try {
      await api.saveUserSettings({ ...settings, selected_virtual_schema_ids: next })
      setSettings({ ...settings, selected_virtual_schema_ids: next })
    } catch (e) {
      showToast?.('error', `설정 저장 실패: ${e.message}`)
    }
  }

  // ── CodeMirror 확장 ─────────────────────────────────────────────────────
  const cmExtensionsOriginal = useMemo(
    () => [..._formatExtension(editOriginalFormat), cmBaseTheme],
    [editOriginalFormat],
  )
  const cmExtensionsSummary = useMemo(
    () => [..._formatExtension(editSummaryFormat), cmBaseTheme],
    [editSummaryFormat],
  )
  const cmTheme = editorTheme || (settings?.theme === 'dark' ? oneDark : 'light')

  // 저장본 미리보기 (snapshot from server) = 우측 panel 의 마지막 저장된 내용 = summary or fallback original
  const savedSummaryText = useMemo(() => {
    if (creating || !detail) return ''
    return detail.summary || detail.original || ''
  }, [creating, detail])

  const savedSummaryFormat = useMemo(() => {
    if (creating || !detail) return editSummaryFormat
    if (detail.summary) return detail.summary_format || 'yaml'
    return detail.original_format || detail.format || 'tsv'  // fallback
  }, [creating, detail, editSummaryFormat])

  const savedFromOriginalFallback = useMemo(() => {
    return detail && !detail.summary
  }, [detail])

  // ── 렌더 ───────────────────────────────────────────────────────────────────
  return (
    <div className="vs-root">
      {/* 좌측 목록 */}
      <aside className="vs-list">
        <div className="vs-list-head">
          <span className="vs-list-title">Virtual Schemas</span>
          <button className="vs-new-btn" onClick={handleNew} title="새 schema 추가">＋ 추가</button>
        </div>
        {/* 검색 / 필터 */}
        <div className="vs-list-search">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="이름 / 설명 / 소유자 (Ctrl+K)"
            total={items.length}
            matched={filteredItems.length}
            compact
          >
            {userId && (
              <button
                type="button"
                className={`search-toggle-btn ${mineOnly ? 'is-on' : ''}`}
                onClick={() => setMineOnly(v => !v)}
                title={mineOnly ? '내 것만 보기 OFF' : '내 것만 보기 ON'}
              >👤 나</button>
            )}
          </SearchInput>
        </div>
        {loadingList && items.length === 0 && (
          <div className="vs-list-empty">로드 중…</div>
        )}
        {!loadingList && items.length === 0 && !creating && (
          <div className="vs-list-empty">등록된 schema 가 없습니다.<br/>＋ 추가 버튼으로 시작하세요.</div>
        )}
        {!loadingList && items.length > 0 && filteredItems.length === 0 && (
          <div className="vs-list-empty">조건에 맞는 schema 가 없습니다.</div>
        )}
        {/* 좌측 목록 — Explore / 다른 좌측 사이드바와 동일한 .std-list-item 디자인.
            다른 사이드바와 다른 점: (1) 우측 ＋사용/✓사용 토글 (2) 요약 배지 (3) line/char/format 메타. */}
        <div className="std-list">
          {creating && (
            <div className="std-list-item active is-creating">
              <div className="std-list-name-row">
                <span className="std-list-name">＋ 새 schema</span>
                <span className="std-list-owner">생성 중</span>
              </div>
            </div>
          )}
          {filteredItems.map(it => {
            const isSel = it.doc_id === selectedId
            const used = isSelectedInSettings(it.doc_id)
            const mine = it.owner_user_id === userId
            const canRename = mine || isAdmin
            const isRenaming = renamingDocId === it.doc_id
            const ownerName = mine ? '' : (it.owner_name || it.owner_user_id || '')
            const ownerTooltip = mine
              ? '내가 만든 schema'
              : `소유자: ${it.owner_name || it.owner_user_id} (${it.owner_user_id})`
            return (
              <div
                key={it.doc_id}
                className={`std-list-item vs-with-toggle ${isSel ? 'active' : ''} ${used ? 'is-used' : ''}`}
                onClick={() => isRenaming ? null : handleSelect(it.doc_id)}
                onDoubleClick={(e) => { if (canRename) { e.stopPropagation(); startRename(it) } }}
                title={it.description || it.preview || it.name}
              >
                <div className="std-list-body">
                  <div className="std-list-name-row">
                    {isRenaming ? (
                      <input
                        className="std-list-rename-input"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter')  { e.preventDefault(); commitRename() }
                          if (e.key === 'Escape') { e.preventDefault(); cancelRename() }
                        }}
                        onBlur={() => commitRename()}
                        autoFocus
                      />
                    ) : (
                      <>
                        <span className="std-list-name">
                          <HighlightText text={it.name} query={query} />
                        </span>
                        {canRename && (
                          <button
                            className="vs-rename-btn"
                            onClick={(e) => { e.stopPropagation(); startRename(it) }}
                            title="이름 바꾸기 (또는 항목 더블클릭)"
                          >✏️</button>
                        )}
                        <span className="std-list-owner" title={ownerTooltip}>
                          {mine
                            ? <span className="std-list-user-badge">나</span>
                            : <HighlightText text={ownerName} query={query} />
                          }
                        </span>
                      </>
                    )}
                  </div>
                  {!isRenaming && it.description && (
                    <div className="std-list-desc" title={it.description}>
                      <HighlightText text={it.description} query={query} />
                    </div>
                  )}
                  <div className="std-list-meta">
                    <span>{it.line_count} 줄</span>
                    <span>· {it.char_count} 자</span>
                    <span>· {it.original_format || 'tsv'}</span>
                    <TokenBadge count={it.token_count} />
                    {it.has_summary && <span className="vs-summary-badge" title="요약본 있음">요약</span>}
                    {it._meta?.updated_at && (
                      <span title={`마지막 수정: ${fmtTime(it._meta.updated_at)}${it._meta?.updated_by ? ` by ${it._meta.updated_by}` : ''}`}>
                        · 🕒 {fmtTime(it._meta.updated_at)}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className={`vs-use-btn ${used ? 'is-on' : ''}`}
                  onClick={(e) => { e.stopPropagation(); handleToggleUse(it) }}
                  title={used ? '사용 중 — 클릭하여 해제' : '사용 안 함 — 클릭하여 LLM 컨텍스트에 포함'}
                >{used ? '✓ 사용' : '＋ 사용'}</button>
              </div>
            )
          })}
        </div>
      </aside>

      {/* 우측 에디터 */}
      <main className="vs-editor">
        {!creating && !selectedId ? (
          <div className="vs-empty">왼쪽에서 schema 를 선택하거나 <strong>＋ 추가</strong> 로 시작하세요.</div>
        ) : (
          <>
            {/* 헤더 — title row (이름 + 저장/삭제 바로 옆) + meta row */}
            <div className="vs-editor-head">
              {/* Row 1: 이름 + 배지 + 저장/삭제 액션 (가까이 배치) */}
              <div className="vs-head-row vs-head-title-row">
                {creating ? (
                  <input
                    className="vs-name-input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="schema 이름"
                    autoFocus
                  />
                ) : (
                  <>
                    <span className="vs-name-display">{detail?.name || '(이름 없음)'}</span>
                    {detail && !isOwnerOrAdmin(detail) && (
                      <span className="vs-readonly-badge" title={`소유자: ${detail.owner_name || detail.owner_user_id}`}>읽기 전용</span>
                    )}
                    {detail && isAdmin && detail.owner_user_id !== userId && (
                      <span className="vs-admin-badge" title="admin 권한으로 편집 가능">admin</span>
                    )}
                  </>
                )}
                {dirty && <span className="vs-dirty-badge">● 수정됨</span>}
                {/* KBNL-69: 본문 헤더 토큰 배지 — 마지막 저장 기준 */}
                {detail && !creating && (
                  <TokenBadge
                    count={detail.token_count}
                    variant="strong"
                    title="이 Virtual Schema 의 저장된 토큰 수 — 편집 후 저장 시 갱신"
                  />
                )}
                <div className="vs-head-actions">
                  <button
                    className="btn-primary"
                    onClick={handleSave}
                    disabled={!dirty || saving || (!canEditSelected && !creating)}
                    title={creating ? '새 schema 생성' : '변경 사항 Firestore 저장'}
                  >{saving ? '저장 중…' : '저장'}</button>
                  {!creating && detail && isOwnerOrAdmin(detail) && (
                    <button
                      className="btn-secondary vs-danger"
                      onClick={handleDelete}
                      disabled={saving}
                    >🗑 삭제</button>
                  )}
                </div>
                <span style={{ flex: 1 }} />
              </div>

              {/* Row 2: meta — 소유자 / 크기 / 생성 / 수정 */}
              {detail && !creating && (
                <div className="vs-head-row vs-meta-block">
                  <span className="vs-meta-row">
                    <span className="vs-meta-key">소유자</span>
                    <span className="vs-owner-name">
                      {detail.owner_name || detail.owner_user_id}
                    </span>
                    {detail.owner_user_id !== userId && (
                      <code className="vs-meta-id">({detail.owner_user_id})</code>
                    )}
                  </span>
                  <span className="vs-meta-sep">·</span>
                  <span className="vs-meta-row">
                    <span className="vs-meta-key">크기</span>
                    {detail.line_count} 줄 / {detail.char_count} 자
                  </span>
                  {detail._meta?.created_at && (
                    <>
                      <span className="vs-meta-sep">·</span>
                      <span className="vs-meta-row">
                        <span className="vs-meta-key">생성</span>
                        {fmtTime(detail._meta.created_at)}
                      </span>
                    </>
                  )}
                  {detail._meta?.updated_at && (
                    <>
                      <span className="vs-meta-sep">·</span>
                      <span className="vs-meta-row">
                        <span className="vs-meta-key">수정</span>
                        {fmtTime(detail._meta.updated_at)}
                        {detail._meta?.updated_by && detail._meta.updated_by !== detail.owner_user_id && (
                          <span className="vs-meta-by"> by <code>{detail._meta.updated_by}</code></span>
                        )}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* 설명 입력 (한 줄, 짧은 메모) */}
            <div className="vs-desc-bar">
              <label htmlFor="vs-desc-input" className="vs-desc-label">설명</label>
              <input
                id="vs-desc-input"
                className="vs-desc-input"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder={canEditSelected
                  ? '이 schema 가 무엇인지 한 줄 메모 (저장 시 함께 기록)'
                  : '(설명 없음)'}
                disabled={!canEditSelected || saving}
                maxLength={500}
              />
            </div>

            {/* 2 panel CodeMirror */}
            <div className="vs-panels">
              {/* 좌 panel: 원본 */}
              <div className="vs-panel">
                <div className="vs-panel-head">
                  <span className="vs-panel-title">원본 (편집)</span>
                  <label className="vs-panel-format">
                    포맷
                    <select
                      value={editOriginalFormat}
                      onChange={(e) => setEditOriginalFormat(e.target.value)}
                      disabled={!canEditSelected || saving}
                    >
                      {FORMAT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </label>
                  <span className="vs-panel-hint">{editOriginal.length} 자</span>
                  <span style={{ flex: 1 }} />
                  {/* 원본 panel 다운로드 (KBNL-27) */}
                  <button
                    className="btn-xs"
                    onClick={() => downloadText(
                      ensureExtension(`${(detail?.name || editName || 'schema')}.original`, editOriginalFormat || 'txt'),
                      editOriginal,
                      'text/plain',
                    )}
                    disabled={!editOriginal}
                    title="원본 panel 내용을 로컬 파일로 다운로드"
                  >⤓</button>
                </div>
                <div className="vs-panel-cm">
                  <CodeMirror
                    value={editOriginal}
                    onChange={canEditSelected ? setEditOriginal : undefined}
                    readOnly={!canEditSelected || loadingDetail}
                    height="100%"
                    theme={cmTheme}
                    extensions={cmExtensionsOriginal}
                    basicSetup={{
                      lineNumbers: true,
                      foldGutter: false,
                      highlightActiveLine: canEditSelected,
                      autocompletion: false,
                    }}
                  />
                  {loadingDetail && <div className="vs-loading">로드 중…</div>}
                </div>
              </div>

              {/* 우 panel: 적용본 (LLM 이 실제로 받는 텍스트) */}
              <div className="vs-panel">
                <div className="vs-panel-head">
                  <span className="vs-panel-title" title="LLM 프롬프트에 실제로 들어가는 텍스트. 저장 후 적용됨.">
                    적용본 (LLM 입력)
                  </span>
                  <label className="vs-panel-format">
                    포맷
                    <select
                      value={editSummaryFormat}
                      onChange={(e) => setEditSummaryFormat(e.target.value)}
                      disabled={!canEditSelected || saving}
                    >
                      {FORMAT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </label>
                  <span className="vs-panel-hint">{editSummary.length} 자</span>
                  <span style={{ flex: 1 }} />
                  {/* 적용본 panel 다운로드 (KBNL-27) */}
                  <button
                    className="btn-xs"
                    onClick={() => downloadText(
                      ensureExtension(`${(detail?.name || editName || 'schema')}.summary`, editSummaryFormat || 'txt'),
                      editSummary,
                      'text/plain',
                    )}
                    disabled={!editSummary}
                    title="적용본 panel 내용을 로컬 파일로 다운로드"
                  >⤓</button>
                </div>
                <div className="vs-panel-cm">
                  <CodeMirror
                    value={editSummary}
                    onChange={canEditSelected ? setEditSummary : undefined}
                    readOnly={!canEditSelected || loadingDetail || summarizing}
                    height="100%"
                    theme={cmTheme}
                    extensions={cmExtensionsSummary}
                    basicSetup={{
                      lineNumbers: true,
                      foldGutter: false,
                      highlightActiveLine: canEditSelected,
                      autocompletion: false,
                    }}
                    placeholder="LLM 에 전달될 텍스트 — '원본 그대로 적용' 또는 '요약해서 적용' 또는 직접 입력"
                  />
                  {summarizing && (
                    <div className="vs-loading">
                      <span className="vs-spinner" /> 요약 중…
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 서버 적용본 상태 (snapshot — 마지막 저장 시점) */}
            {!creating && detail && (
              <div className="vs-saved-bar" title="LLM 이 현재 실제로 받는 텍스트 (마지막 저장 시점). 우측 panel 을 편집해도 저장 전까지 이 내용을 사용.">
                <span className="vs-saved-label">
                  📦 서버 적용본
                  {savedFromOriginalFallback && (
                    <span className="vs-active-version"> — 원본 fallback (적용본 비어있음)</span>
                  )}
                </span>
                <span className="vs-saved-meta">
                  {savedSummaryText.length} 자 · {savedSummaryFormat}
                </span>
                {dirty && <span className="vs-saved-dirty">⚠ 미저장 변경 있음 — 저장 전까지 위 내용을 LLM 이 사용</span>}
              </div>
            )}

            {/* 푸터 — 컨텐츠 조작 액션 (적용본 panel 에 내용 채우기) */}
            {canEditSelected && (
              <div className="vs-editor-foot">
                <span className="vs-foot-label">적용본 채우기 →</span>
                <button
                  className="btn-secondary"
                  onClick={handleApplyOriginal}
                  disabled={saving || summarizing || !editOriginal}
                  title="좌측 원본 텍스트를 우측 적용본 panel 에 그대로 복사 (저장 X — 상단 '저장' 으로 영속)"
                >원본 그대로 적용</button>
                <button
                  className="btn-secondary"
                  onClick={handleApplySummary}
                  disabled={saving || summarizing || !editOriginal || creating}
                  title={creating
                    ? '먼저 schema 를 저장한 뒤 요약 가능'
                    : 'LLM 으로 좌측 원본을 요약 → 우측 적용본 panel 에 채움'}
                >
                  {summarizing && <span className="vs-spinner-inline" />}
                  {summarizing ? ' 요약 중…' : '요약해서 적용'}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}
